import React, { useEffect, useRef, useState } from "react"
import { scopeUrl } from "lib/scope-url"
import { Box, CircularProgress, DialogContent, DialogTitle, Paper } from "@mui/material"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { useDialog } from "lib/@hooks/useDialog"
import { Done } from "lib/@components/done"
import { supportedFilePreviews, supportedImagePreviews } from "./file-types"

export function usePreview(filename, name = filename, extension = null) {
    return useDialog(<PreviewDialog name={name} url={filename} extension={extension} />, {
        maxWidth: "lg",
        fullScreen: true,
    })
}

export function PreviewDialog({ url, name, extension }) {
    const [iframe, setIframe] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    extension = (extension ?? url.split(".").at(-1)).toLowerCase()
    const scopedUrl = scopeUrl(url)
    const count = useRef(0)
    const [id, setId] = useState(1)

    const hasSupportedImage = supportedImagePreviews.includes(extension)
    const hasSupportedPdf = extension === "pdf"
    const hasSupportedFile = supportedFilePreviews.includes(extension) && !hasSupportedPdf

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(setupDocumentPreview, [iframe, scopedUrl, hasSupportedFile])

    return (
        <>
            <DialogTitle>{name}</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                {hasSupportedImage && (
                    <Box
                        component="img"
                        sx={{ border: "none", width: 1, flex: 1, maxHeight: "80vh", objectFit: "contain" }}
                        src={scopedUrl}
                        alt={name}
                    />
                )}
                {hasSupportedPdf && (
                    <Paper
                        elevation={8}
                        component="iframe"
                        src={scopedUrl}
                        sx={{ border: "none", borderRadius: 1, width: 1, flex: 1 }}
                        title="Preview Document"
                    />
                )}
                {hasSupportedFile && (
                    <Paper
                        key={id}
                        elevation={8}
                        component="iframe"
                        ref={setIframe}
                        sx={{ border: "none", borderRadius: 1, width: 1, flex: 1 }}
                        title="Preview Document"
                    >
                        <CircularProgress color="primary" />
                    </Paper>
                )}
                {error && (
                    <Box sx={{ p: 3, textAlign: "center" }}>
                        Unable to load preview. Please download the file to view it.
                    </Box>
                )}
                {loading && <LoadingScreen />}
            </DialogContent>
            <Done />
        </>
    )

    function setupDocumentPreview() {
        const controller = new AbortController()
        loadDocumentPreview(controller.signal)
        return () => controller.abort()
    }

    async function loadDocumentPreview(signal) {
        if (!hasSupportedFile) {
            setLoading(false)
            return
        }
        if (!iframe) return
        count.current = 0
        setLoading(true)
        setError(false)
        try {
            const response = await fetch(`/s3/preview-url?url=${encodeURIComponent(url)}`, { signal })
            if (!response.ok) throw new Error(await response.text())
            const { token } = await response.json()
            if (signal.aborted) return
            const previewUrl = `${scopedUrl}?previewToken=${token}`
            iframe.src = `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(previewUrl)}`
            setTimeout(scanIframe, 1500)
        } catch (e) {
            if (signal.aborted || e.name === "AbortError") return
            console.error("Failed to load document preview", e)
            setLoading(false)
            setError(true)
        }
    }

    function scanIframe() {
        try {
            if (iframe.contentDocument.URL !== "about:blank") {
                if (iframe.contentDocument?.body?.childElementCount > 0) {
                    throw new Error("Loaded")
                }
            }
            if (count.current++ > 7) {
                setId((id) => id + 1)
                return
            }
            setTimeout(() => scanIframe(), 500)
        } catch (e) {
            setLoading(false)
        }
    }
}
